import type { Transaction } from '@tanstack/react-db'
import {
    type UseMutationOptions,
    type UseMutationResult,
    useMutation as useTanStackMutation,
} from '@tanstack/react-query'

/**
 * Generator-based mutation utility that automatically awaits each operation
 * Eliminates the need for explicit await keywords and supports both sequential and parallel execution
 *
 * @example Sequential operations
 * ```ts
 * const result = await performMutations(function* () {
 *   const addressId = newRecordId()
 *   yield addressesCollection.insert({ id: addressId, ... })
 *   yield customersCollection.insert({ address: addressId, ... })
 *   return { addressId }
 * })
 * ```
 *
 * @example Parallel operations (yield an array)
 * ```ts
 * const result = await performMutations(function* () {
 *   // These three updates can happen in parallel
 *   yield [
 *     customersCollection.update(id1, draft => { ... }),
 *     customersCollection.update(id2, draft => { ... }),
 *     addressesCollection.update(id3, draft => { ... })
 *   ]
 *   return { updated: 3 }
 * })
 * ```
 *
 * @example Mixed sequential and parallel
 * ```ts
 * const result = await performMutations(function* () {
 *   const addressId = newRecordId()
 *   // First, create the address (sequential)
 *   yield addressesCollection.insert({ id: addressId, ... })
 *
 *   // Then create multiple customers in parallel that reference the address
 *   const customer1Id = newRecordId()
 *   const customer2Id = newRecordId()
 *   yield [
 *     customersCollection.insert({ id: customer1Id, address: addressId, ... }),
 *     customersCollection.insert({ id: customer2Id, address: addressId, ... })
 *   ]
 *
 *   return { addressId, customerIds: [customer1Id, customer2Id] }
 * })
 * ```
 */
export async function performMutations<TResult = void>(
    fn: () => Generator<
        Transaction<Record<string, unknown>> | Transaction<Record<string, unknown>>[],
        TResult,
        void
    >
): Promise<TResult> {
    const gen = fn()

    let result = gen.next()
    while (!result.done) {
        const value = result.value

        // If it's an array, await all transactions in parallel
        if (Array.isArray(value)) {
            await Promise.all(value.map(tx => tx.isPersisted.promise))
        } else {
            // Single transaction, await sequentially
            await value.isPersisted.promise
        }

        result = gen.next()
    }

    return result.value
}

type GeneratorMutationOptions<TData = unknown, TError = Error, TVariables = void> = Omit<
    UseMutationOptions<TData, TError, TVariables>,
    'mutationFn'
> & {
    mutationFn: (
        variables: TVariables
    ) => Generator<
        Transaction<Record<string, unknown>> | Transaction<Record<string, unknown>>[],
        TData,
        void
    >
}

type AsyncMutationOptions<TData = unknown, TError = Error, TVariables = void> = UseMutationOptions<
    TData,
    TError,
    TVariables
>

/**
 * Mutation hook that wraps TanStack Query's useMutation
 * Supports both generator-based and async mutation functions
 *
 * @example Generator-based mutation (auto-wrapped with performMutations)
 * ```ts
 * const createCustomer = useMutation({
 *     mutationFn: function* (data: CustomerFormData) {
 *         const addressId = newRecordId()
 *         yield addressesCollection.insert({ id: addressId, ...data.address })
 *         const customerId = newRecordId()
 *         yield customersCollection.insert({ id: customerId, address: addressId, ...data })
 *         return { customerId, addressId }
 *     },
 *     onSuccess: (result) => {
 *         console.log('Created customer:', result.customerId)
 *     },
 * })
 * ```
 *
 * @example Async mutation (standard TanStack Query)
 * ```ts
 * const createCustomer = useMutation({
 *     mutationFn: async (data: CustomerFormData) => {
 *         const locationData = await geocodeAddress(data.address)
 *         return await performMutations(function* () {
 *             yield customersCollection.insert({ ...data })
 *         })
 *     },
 *     onSuccess: () => {
 *         router.push('/customers')
 *     },
 * })
 * ```
 */
export function useMutation<TData = unknown, TError = Error, TVariables = void>(
    options:
        | GeneratorMutationOptions<TData, TError, TVariables>
        | AsyncMutationOptions<TData, TError, TVariables>
): UseMutationResult<TData, TError, TVariables> {
    const { mutationFn, ...restOptions } = options

    const isGeneratorFn = mutationFn && mutationFn.constructor.name === 'GeneratorFunction'

    const wrappedOptions = isGeneratorFn
        ? {
              ...restOptions,
              mutationFn: async (variables: TVariables) => {
                  return await performMutations(() =>
                      (
                          mutationFn as (
                              variables: TVariables
                          ) => Generator<
                              | Transaction<Record<string, unknown>>
                              | Transaction<Record<string, unknown>>[],
                              TData,
                              void
                          >
                      )(variables)
                  )
              },
          }
        : (options as AsyncMutationOptions<TData, TError, TVariables>)

    return useTanStackMutation(wrappedOptions)
}
